
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { FileInterface, FolderInterface } from '@/components/file-browser/interfaces';
import { Treeview } from '@/views/interfaces/vuetify/treeview.interface';
import CreateFolder, { CreateFolderForm } from '@/views/pages/internal-file-manager/components/CreateFolder.vue';
import AddFile, { AddFileForm } from '@/views/pages/internal-file-manager/components/AddFile.vue';
import FileIcon from '@/views/pages/internal-file-manager/components/FileIcon.vue';

export type FolderTreeBranch = Treeview & { folder: FolderInterface, parent?: FolderTreeBranch };

@Component({
  components: { FileIcon, AddFile, CreateFolder },
})
export default class FileBrowser extends Vue {
  @Prop({ required: true })
  private readonly fetchFolderCallback: (id: string | number) => Promise<FolderInterface>;

  @Prop({ required: false })
  private readonly onCreateFolder?: (data: CreateFolderForm) => Promise<FolderInterface>;

  @Prop({ required: false })
  private readonly onUploadFile?: (data: AddFileForm) => Promise<FileInterface>;

  @Prop({ required: false })
  private readonly onDeleteFolder?: (folder: FolderInterface) => Promise<void>;

  @Prop({ required: false })
  private readonly onDeleteFile?: (file: FileInterface) => Promise<void>;

  @Prop({ required: false })
  private readonly onDownloadFile?: (file: FileInterface) => Promise<void>;

  @Prop({ required: true })
  private rootFolders: FolderInterface[];

  @Prop({ required: false, default: () => false })
  private readonly canEditFilePermissions!: boolean;

  private currentHover?: number | string = 0;

  private folders: FolderTreeBranch[] = [];

  private creatingFolder: boolean = false;

  private creatingFolderParent?: FolderTreeBranch = undefined;

  private addingFile: boolean = false;

  private addFileFolder?: FolderTreeBranch = undefined;

  private deletingFolder: boolean = false;

  private folderToBeDeleted?: FolderTreeBranch = undefined;

  private deletingFile: boolean = false;

  private fileToBeDeleted?: FileInterface & { parent?: FolderTreeBranch } = undefined;

  private editingFile: boolean = false;

  private fileBeingEdited?: FileInterface = undefined;

  @Watch('rootFolders', { immediate: true, deep: true })
  private updateFolders(): void {
    this.folders = this.rootFolders.map((folder) => this.folderToTree(folder));
  }

  private folderToTree(folder: FolderInterface, parent?: FolderTreeBranch): FolderTreeBranch {
    const tree: FolderTreeBranch = {
      folder,
      parent,
      id: folder.id,
      name: folder.name,
      children: [
        ...(folder.folders ?? []).map((childFolder) => this.folderToTree(childFolder)),
        ...(folder.files ?? []).map((file: FileInterface) => {
          return {
            containingFolder: folder,
            id: `file_${file.id}`,
            name: file.originalName,
            path: file.path,
            mimeType: file.mimeType,
            extension: file.extension,
          }
        }),
      ],
    };

    tree.children?.forEach((child) => {
      child.parent = tree;
    });

    return tree;
  }

  private async getContent(folder: FolderTreeBranch): Promise<void> {
    const folderData = await this.fetchFolderCallback(folder.id);

    (folder.folder as FolderInterface).files = folderData.files;
    (folder.folder as FolderInterface).folders = folderData.folders;
    folder.children?.push(...(this.folderToTree(folderData).children ?? []));
  }

  private async uploadFileDialog(folder: FolderTreeBranch): Promise<void> {
    this.addingFile = true;
    this.addFileFolder = folder;
  }

  private async uploadFile(addFile: AddFileForm): Promise<void> {
    try {
      await this.onUploadFile?.(addFile);


      if (this.addFileFolder) {
      const updatedFolder = await this.fetchFolderCallback(addFile.folderId);
        this.addFileFolder.children = this.folderToTree(updatedFolder).children;
        Object.assign(this.addFileFolder?.folder, {
          files: updatedFolder.files,
          folders: updatedFolder.folders,
        });
      }

      this.addingFile = false;
      this.addFileFolder = undefined;
      this.$successDialog.show('File has been uploaded.');
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private async createNewFolderDialog(folder?: FolderTreeBranch): Promise<void> {
    this.creatingFolder = true;
    this.creatingFolderParent = folder;
  }

  private async createFolder(data: CreateFolderForm): Promise<void> {
    try {
      const newFolder = await this.onCreateFolder?.(data);
      this.$successDialog.show(`Successfully created new folder '${data.name}'`);

      if (!this.creatingFolderParent) {
        this.$emit('refresh-root-folders')
      } else {
        if (newFolder && this.creatingFolderParent.children) {
          let position = 0;
          this.creatingFolderParent.children.findIndex((item, index) => {
            if (!item.folder) {
              position = index - 1;
              return 0;
            }
          });

          this.creatingFolderParent.children.splice(position, 0, this.folderToTree(newFolder, this.creatingFolderParent));
        }
      }

      this.creatingFolder = false;
      this.creatingFolderParent = undefined;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  private async deleteFolderDialog(folder: FolderTreeBranch): Promise<void> {
    this.deletingFolder = true;
    this.folderToBeDeleted = folder;
  }

  private async deleteFolder(): Promise<void> {
    try {
      if (!this.folderToBeDeleted) {
        return;
      }

      await this.onDeleteFolder?.(this.folderToBeDeleted.folder);
      this.$infoDialog.show(`Folder '${this.folderToBeDeleted.name}' has been deleted.`);
      if (this.folderToBeDeleted.parent && this.folderToBeDeleted.parent.children) {
        this.folderToBeDeleted.parent.children = this.folderToBeDeleted.parent.children.filter(
          (child) => child.id !== this.folderToBeDeleted?.id,
        );
      }

      this.deletingFolder = false;
      this.folderToBeDeleted = undefined;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message ?? err.message);
    }
  }

  private async deleteFileDialog(file: FileInterface): Promise<void> {
    this.deletingFile = true;
    this.fileToBeDeleted = file;
  }

  private async deleteFile(): Promise<void> {
    try {
      const deletedFile = Object.assign(
        this.fileToBeDeleted as unknown as FileInterface & { parent: FolderTreeBranch },
        { id: (this.fileToBeDeleted?.id as string).split('_').pop() },
      );

      try {
        await this.onDeleteFile?.(deletedFile);
      } catch (e) { /**/ }

      if (this.fileToBeDeleted) {
        const updatedFolder = await this.fetchFolderCallback(this.fileToBeDeleted.containingFolder.id);
        if (this.fileToBeDeleted.parent) {
          this.fileToBeDeleted.parent.children = this.folderToTree(updatedFolder).children;
          Object.assign(this.fileToBeDeleted.containingFolder, {
            files: updatedFolder.files,
            folders: updatedFolder.folders,
          });
        }
      }

      this.$infoDialog.show('File has been deleted');
      this.deletingFile = false;
      this.fileToBeDeleted = undefined;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message ?? err.message);
    }
  }

  private async downloadFile(file: FileInterface): Promise<void> {
    await this.onDownloadFile?.(file);
  }

  private editFile(file: FileInterface): void {
    this.$emit('start-edit', file.id)
    this.editingFile = true;
    this.fileBeingEdited = file;
  }

  private setHover(itemId?: number | string): void {
    this.currentHover = itemId;
  }
}

